import { useState, FunctionComponent, useEffect } from 'react';
import { Stop, AddCircle } from '../../components/icons';
import { DatePicker } from './../../MaterialUiComponents';
import { Autocomplete, TextField, Typography, IconButton, Input, Checkbox, Select, Table, TableRow, TableCell } from './../../tailwindcomponents';
import { palette } from './../../theme/palette';
import { Timer, EmployeeSearch } from './../../components';
import SecurityApi from '../../api/security';
import { callOuts, movements, otherIncidents, securityResult, walkArounds, guestPasses } from './../../types/security';
import { RootState } from '../../reducer';
import { useSelector } from 'react-redux'
import ReportInfo from './ReportInfo';
import EditColumn from './EditColumn';
import AmountControls from './AmountControls';
import { DateTime } from 'luxon';

type ReportProps = {
    selectedReport: securityResult,
    bays: { "name": string, id: number }[]
    closeReport: Function,
    refresh: Function,
    tails: string[],
    isSecurityAdmin: Boolean
}

const Report: FunctionComponent<ReportProps> = ({ selectedReport, closeReport, refresh, bays, tails, isSecurityAdmin }) => {
    const [editingRow, setEditingRow] = useState(false);
    const [editMovementsRow, setEditMovementsRow] = useState(-1);
    const [editCallOutRow, setEditCallOutRow] = useState(-1);
    const [editWalkAroundsRow, setEditWalkAroundsRow] = useState(-1);
    const [editIncidentssRow, setEditIncidentsRow] = useState(-1);
    const [editGuestPassesRow, setEditGuestPassesRow] = useState(-1);
    const store = useSelector((state: RootState) => state);
    const user = store.user.me;
    const [tempReason, setTempReason] = useState<string>("");
    const [tempInside, setTempInside] = useState<boolean>(true);
    const [tempOwner, setTempOwner] = useState<string>("");
    const [tempBay, setTempBay] = useState<number>(0);
    const [tempInDate, setTempInDate] = useState<Date>(new Date());
    const [tempOutDate, setTempOutDate] = useState<Date>(new Date());
    const [inputError, setInputError] = useState<boolean>(false);
    const [validationText, setValidationText] = useState<string>("");
    const [tempTail, setTempTail] = useState<string | null>("");
    const [tempGuestPassName, setTempGuestPassName] = useState<string>("");
    const [tempPassNo, setTempPassNo] = useState<string | undefined>(undefined);
    const [tempResponsibleEmployee, setTempResponsibleEmployee] = useState<number | null>(null);
    const [tempGuestPassReturned, setTempGuestPassReturned] = useState<boolean>(false);
    const [tempGuestPassIssuedDate, setTempGuestPassIssuedDate] = useState<Date>(new Date());
    const [tempGuestPassReturnDate, setTempGuestPassReturnDate] = useState<Date | null>(null);
    


    // Refresh after each edit, and editingRow true closes all other rows for editing
    useEffect(() => {
        refresh();
        if (editMovementsRow === -1 && editCallOutRow === -1 && editWalkAroundsRow === -1 && editIncidentssRow === -1 && editGuestPassesRow === -1) {
            setEditingRow(false);
        }
        else {
            setEditingRow(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editMovementsRow, editCallOutRow, editWalkAroundsRow, editIncidentssRow, editGuestPassesRow]);

    // Table bodies for all four tables comes here. Lot of logic to determine if row is in edit mode or not. Edit pencil icon only displayed in edit mode
    const generateMovementsBody = (row: movements, index: number) => {
        return (
            <TableRow className={editMovementsRow !== -1 && editMovementsRow !== index ? "bg-black/50" : ''}>
                <TableCell>
                    {tempOwner !== "" && editMovementsRow === index ? tempOwner : row.general_info.last_modified_date_by_employee_name !== "" ? row.general_info.last_modified_date_by_employee_name : row.general_info.created_by_employee_name}
                </TableCell>
                <TableCell>{editMovementsRow === index ?
                    <Input multiline={true} help_text={validationText} error={inputError} label_text={"Reason"} value={tempReason} onTextChange={(text: string) => onReasonChange(text)} type="text" width={100} /> : row.reason}</TableCell>
                <TableCell width="130">{editMovementsRow === index ?
                    <Autocomplete
                        fullWidth
                        options={tails}
                        value={tempTail}
                        noOptionsText="No tails found"
                        clearOnEscape
                        autoHighlight
                        onChange={(e: any, newValue: string | null) => setTempTail(newValue)}
                        renderInput={(params) => (
                            <TextField {...params} label="Tails" margin="none" variant="standard" size="small" fullWidth />
                        )} /> : row.tail_number}</TableCell>
                <TableCell>{editMovementsRow === index ?
                    <Select
                        selectedItem={tempBay}
                        width={100}
                        filter_by_text={"Bays"}
                        optionsObject={bays}
                        onSelectedItem={(e: any) => setBay(e.target.value)} >
                    </Select> : row.bay_name}
                </TableCell>
                <TableCell width="20">
                    <Checkbox color="primary" disabled={editMovementsRow === index ? false : true} checked={editMovementsRow === index ? tempInside : row.aircraft_moved_inside} onChange={() => onMovementsCheck(index)} />
                </TableCell>
                <TableCell>{row.general_info.last_modified_date !== null ? row.general_info.last_modified_date_text : row.general_info.created_date_text}</TableCell>
                {isSecurityAdmin && !selectedReport.report.closed &&
                    <EditColumn
                        index={index}
                        saveChanges={() => saveChanges("movement", index)}
                        deleteRow={() => deleteRow("movement", index)}
                        editRow={editMovementsRow}
                        setEditRow={(editRow: number) => { onEditRow("movement", index) }} />
                }
            </TableRow>
        )
    }
    const generateCallOutBody = (row: callOuts, index: number) => {
        return (
            <TableRow className={editCallOutRow !== -1 && editCallOutRow !== index ? "bg-black/50" : ''}>
                <TableCell>{editCallOutRow === index ? <EmployeeSearch defaultEmployee={row.call_out_employee_name} inputWidth={110} place_holder="Enter Name" onSelectedEmployee={(employee_id: number) => onSelectedEmployee(employee_id, index)} /> : row.call_out_employee_name} </TableCell>
                <TableCell>{editCallOutRow === index ? <Input multiline={true} help_text={validationText} error={inputError} label_text={"Reason"} value={tempReason} onTextChange={(text: string) => onReasonChange(text)} type="text" width={100} /> : row.reason}</TableCell>
                <TableCell width={130}>{editCallOutRow === index ? <DatePicker type="DateAndTime" selected={tempInDate} label="Time" onDateChange={(date: Date) => { setTempInDate(date) }} /> : row.employee_in_date_text}</TableCell>
                <TableCell width={130}>{editCallOutRow === index ? <DatePicker type="DateAndTime" selected={tempOutDate} label="Time" onDateChange={(date: Date) => { setTempOutDate(date) }} /> : row.employee_out_date_text}</TableCell>
                <TableCell>{row.general_info.last_modified_date !== null ? row.general_info.last_modified_date_text : row.general_info.created_date_text}</TableCell>
                {isSecurityAdmin && !selectedReport.report.closed &&
                    <EditColumn
                        index={index}
                        saveChanges={() => saveChanges("callOut", index)}
                        deleteRow={() => deleteRow("callOut", index)}
                        editRow={editCallOutRow}
                        setEditRow={(editRow: number) => { onEditRow("callOut", index) }} />
                }
            </TableRow>
        )
    }
    const generateWalkAroundBody = (row: walkArounds, index: number) => {
        return (
            <TableRow className={editWalkAroundsRow !== -1 && editWalkAroundsRow !== index ? "bg-black/50" : ''}>
                <TableCell>
                    {tempOwner !== "" && editMovementsRow === index ? tempOwner : row.general_info.last_modified_date_by_employee_name !== "" ? row.general_info.last_modified_date_by_employee_name : row.general_info.created_by_employee_name}
                </TableCell>
                <TableCell>{editWalkAroundsRow === index ? <Input multiline={true} help_text={validationText} error={inputError} label_text={"Type a note"} value={tempReason} onTextChange={(text: string) => onReasonChange(text)} type="text" width={100} /> : row.note}</TableCell>
                <TableCell>{row.stop_date_text === "" ? <Timer startTime={row.general_info.created_date} /> : row.general_info.created_date_text?.substring(11, 16)}</TableCell>
                <TableCell>{row.stop_date_text?.substring(11, 16)}</TableCell>
                <TableCell>{row.general_info.last_modified_date !== null ? row.general_info.last_modified_date_text : row.general_info.created_date_text}</TableCell>
                {isSecurityAdmin && !selectedReport.report.closed &&
                    <EditColumn
                        index={index}
                        saveChanges={() => saveChanges("walkAround", index)}
                        customIcon={row.stop_date_text === "" ? <Stop color="error" /> : undefined}
                        deleteRow={() => deleteRow("walkAround", index)}
                        editRow={editWalkAroundsRow}
                        setEditRow={(editRow: number) => { row.stop_date_text === "" ? onEditRow("walkAroundStop", index) : onEditRow("walkAround", index) }} />
                }
            </TableRow>
        )
    }
    const generateIncidentsBody = (row: otherIncidents, index: number) => {
        return (
            <TableRow className={editIncidentssRow !== -1 && editIncidentssRow !== index ? "bg-black/50" : ''}>
                <TableCell>
                    {tempOwner !== "" && editMovementsRow === index ? tempOwner : row.general_info.last_modified_date_by_employee_name !== "" ? row.general_info.last_modified_date_by_employee_name : row.general_info.created_by_employee_name}
                </TableCell>
                <TableCell>{editIncidentssRow === index ? <Input multiline={true} help_text={validationText} error={inputError} label_text={"Type a description"} value={tempReason} onTextChange={(text: string) => onReasonChange(text)} type="text" width={100} /> : row.incident}</TableCell>
                <TableCell>{row.general_info.last_modified_date !== null ? row.general_info.last_modified_date_text : row.general_info.created_date_text}</TableCell>
                {isSecurityAdmin && !selectedReport.report.closed &&
                    <EditColumn
                        index={index}
                        editRow={editIncidentssRow}
                        saveChanges={() => saveChanges("incident", index)}
                        deleteRow={() => deleteRow("incident", index)}
                        setEditRow={(editRow: number) => { onEditRow("incident", index) }} />
                }
            </TableRow>
        )
    }

    const generateGestPassesBody = (row: guestPasses, index: number) => {
        return (
            <TableRow className={editGuestPassesRow !== -1 && editGuestPassesRow !== index ? "bg-black/50" : ''}>
                <TableCell>{editGuestPassesRow === index ?
                    <Input multiline={true} help_text={validationText} error={inputError} label_text={"Name"} value={tempGuestPassName} onTextChange={(text: string) => setTempGuestPassName(text)} type="text" width={100} /> : row.guestName}
                </TableCell>
                <TableCell>{editGuestPassesRow === index ?
                    <Input multiline={true} help_text={validationText} error={inputError} label_text={"Pass no."} value={tempPassNo} onTextChange={(text: string | undefined) => setTempPassNo(text)} type="text" width={100} /> : row.passNumber}
                </TableCell>
                <TableCell width="130">{editGuestPassesRow === index ?
                    <EmployeeSearch
                        inputWidth={130}
                        place_holder={"Responsible person"}
                        onSelectedEmployee={(employeeId: number) => setTempResponsibleEmployee(employeeId || null)}
                        defaultEmployee={row.responsibleEmployeeName ?? ""}
                    />
                    : row.responsibleEmployeeName}
                </TableCell>
                <TableCell>
                    {editGuestPassesRow === index ? (
                        <DatePicker
                        type="DateAndTime"
                        selected={tempGuestPassIssuedDate}
                        label="Issued"
                        onDateChange={(date: Date) => setTempGuestPassIssuedDate(date)}
                        />
                    ) : (
                        row.issuDate
                        ? DateTime.fromJSDate(new Date(row.issuDate)).toFormat('dd.LL.yyyy HH:mm:ss')
                        
                        : "-"
                    )}
                </TableCell>
                <TableCell width="20">
                    <Checkbox color="primary" disabled={editGuestPassesRow === index ? false : true} checked={editGuestPassesRow === index ? tempGuestPassReturned : (row.returnDate != null ? true : false)} onChange={() => onReturnGuestPassCheck(index)} />
                </TableCell>
                <TableCell>
                    {editGuestPassesRow === index ? (
                        <DatePicker
                        type="DateAndTime"
                        selected={tempGuestPassReturnDate}
                        label="Returned in"
                        onDateChange={(date: Date) => setTempGuestPassReturnDate(date)}
                        />
                    ) : (
                        row.returnDate
                        ? DateTime.fromJSDate(new Date(row.returnDate)).toFormat('dd.LL.yyyy HH:mm:ss')
                        : "-"
                    )}
                    </TableCell>
                {isSecurityAdmin && !selectedReport.report.closed &&
                    <EditColumn
                        index={index}
                        editRow={editGuestPassesRow}
                        saveChanges={() => saveChanges("guestPasses", index)}
                        deleteRow={() => deleteRow("guestPasses", index)}
                        setEditRow={() => { onEditRow("guestPasses", index) }} />
                }
            </TableRow>
        )
    }

    const onSelectedEmployee = (employeeId: number, index: number) => {
        if (!employeeId) return;
        selectedReport.call_outs[index - 1].outcall_employee_id = employeeId;
    }

    // Conditional edit for each table type based on row index number. Temp state of same types re-used for each one, as only 1 row can be edited at a time. 
    // This only sets the edit row and sets values based on that. Each edit is then in seperate functions.
    // For example tempReason is re-used in each type. The edit row is locked until either changes are saved or row deleted.
    const onEditRow = (type: string, index: number) => {
        setInputError(false);
        setValidationText("");

        if (type === "movement" && editingRow === false) {
            let tempRow = selectedReport.movements[index - 1];
            setTempTail(tempRow.tail_number);
            setTempInside(tempRow.aircraft_moved_inside);
            setTempBay(tempRow.bay_id);
            setTempReason(tempRow.reason);
            setEditMovementsRow(index);
        }
        if (type === "callOut" && editingRow === false) {
            let tempRow = selectedReport.call_outs[index - 1];
            setTempInDate(tempRow.employee_in_date);
            setTempOutDate(tempRow.employee_out_date);
            setTempReason(tempRow.reason);
            setEditCallOutRow(index);
        }
        if (type === "walkAround" && editingRow === false) {
            let tempRow = selectedReport.walk_arounds[index - 1];
            setTempReason(tempRow.note);
            setEditWalkAroundsRow(index);
        }
        if (type === "incident" && editingRow === false) {
            let tempRow = selectedReport.other_incidents[index - 1];
            setTempReason(tempRow.incident);
            setEditIncidentsRow(index);
        }
        // Special logic for stopping walkaround
        if (type === "walkAroundStop") {
            let tempRow = selectedReport.walk_arounds[index - 1];
            SecurityApi.WalkAroundFinishedRound(tempRow.walk_around_id, user.user_id, selectedReport.report.report_id).then(res => {
                setEditWalkAroundsRow(index);
            });
        }
        if (type === "guestPasses" && editingRow === false) {
            let tempRow = selectedReport.guest_passes[index - 1];
            setTempGuestPassName(tempRow.guestName);
            setTempPassNo(tempRow.passNumber);
            setTempResponsibleEmployee(tempRow.responsibleEmployeeId);
            setTempGuestPassIssuedDate(tempRow.issuDate ? new Date(tempRow.issuDate) : new Date());
            setTempGuestPassReturnDate(tempRow.returnDate ? new Date(tempRow.returnDate) : null);
            setTempGuestPassReturned(tempRow.returnDate !== null ? true : false);
            setEditGuestPassesRow(index);
        }

        // Owner is always changed to the person that does the last edit on the row
        setTempOwner(user.display_name);
    }
    const onMovementsCheck = (index: number) => {
        setTempInside(!tempInside);
        selectedReport.movements[index - 1].aircraft_moved_inside = !tempInside;
    }
    const setBay = (value: number) => {
        setTempBay(value);
    }
    const onReasonChange = (text: string) => {
        setTempReason(text);
    }
    const submitReport = () => {
        SecurityApi.CloseDailyReport(user.user_id).then(res => {
            refresh();
        });
    }

    const onReturnGuestPassCheck = (index: number) => {
        setTempGuestPassReturned(!tempGuestPassReturned);
        if(tempGuestPassReturned) {
            setTempGuestPassReturnDate(null);
        }
        else {
            setTempGuestPassReturnDate(new Date());
        }
    }

    // Takes in table type and row index and uses the temp variables to submit changes through API.
    // Validation check for empty text string
    const saveChanges = (type: string, index: number) => {
        if ((tempReason === "" || tempReason === undefined) && (!tempGuestPassName || !tempPassNo)  ) { 
            setInputError(true);
            setValidationText("missing field");
        }
        else if (type === "movement") {
            const movement = selectedReport.movements[index - 1];
            SecurityApi.AircraftMovementsUpdateRow(movement.movement_id, user.user_id, tempTail !== null ? tempTail : "", tempBay, tempInside, tempReason, selectedReport.report.report_id).then(res => {
                setEditMovementsRow(-1);
            });
        }
        else if (type === "callOut") {
            const callOut = selectedReport.call_outs[index - 1];
            SecurityApi.CallOutUpdateRow(user.user_id, callOut.outcall_employee_id, tempInDate, tempOutDate, tempReason, selectedReport.report.report_id, callOut.call_outs_id).then(res => {
                setEditCallOutRow(-1);
            });
        }
        else if (type === "walkAround") {
            const walkAround = selectedReport.walk_arounds[index - 1];
            SecurityApi.WalkAroundUpdateRow(walkAround.walk_around_id, user.user_id, walkAround.stop_date, walkAround.stop_date_by_employee_id, tempReason, selectedReport.report.report_id).then(res => {
                setEditWalkAroundsRow(-1);
            });
        }
        else if (type === "incident") {
            const incident = selectedReport.other_incidents[index - 1];
            SecurityApi.OtherIncidentsUpdateRow(incident.other_incident_id, user.user_id, tempReason, selectedReport.report.report_id).then(res => {
                setEditIncidentsRow(-1);
            });
        }
        else if (type === "guestPasses") {
            const guestPass = selectedReport.guest_passes[index - 1];
            SecurityApi.GuestPassesUpdateRow(guestPass.id, tempGuestPassName, selectedReport.report.report_id, tempPassNo, tempResponsibleEmployee, tempGuestPassIssuedDate, tempGuestPassReturnDate).then(res => {
                setTempGuestPassName("");
                setTempPassNo("");
                setTempResponsibleEmployee(null);
                setTempGuestPassIssuedDate(new Date());
                setTempGuestPassReturnDate(null);
                setTempGuestPassReturned(false);
                setEditGuestPassesRow(-1);
            });
        }
    }
    // Takes in table type and creates new item through api with empty reason. That triggers refresh of the report. 
    // New row is set to edit mode so reason must be added before saving or else delete the row. 
    const addRow = (type: string) => {
        setTempReason("");
        setTempBay(0);
        setTempInside(false);
        setInputError(false);
        setValidationText("");
        if (type === "movement") {
            SecurityApi.AircraftMovementsCreateNewRow(user.user_id, "", 1, true, "", selectedReport.report.report_id).then(res => {
                setEditMovementsRow(selectedReport.movements.length + 1);
            });
        }
        if (type === "callOut") {
            SecurityApi.CallOutCreateNewRow(user.user_id, user.user_id, new Date(), new Date(), "", selectedReport.report.report_id).then(res => {
                setTempInDate(new Date());
                setTempOutDate(new Date());
                setEditCallOutRow(selectedReport.call_outs.length + 1);
            });
        }
        // This is not set to Edit row immediately because this set's in effect a timer that runs while walk around is in progress and displays a stop buutton
        // When stop button is clicked the row is set to edit mode
        if (type === "walkAround") {
            SecurityApi.WalkAroundCreateNewRow(user.user_id, "", selectedReport.report.report_id).then(res => {
                refresh();
            });
        }
        if (type === "incident") {
            SecurityApi.OtherIncidentsCreateNewRow(user.user_id, "", selectedReport.report.report_id).then(res => {
                setEditIncidentsRow(selectedReport.other_incidents.length + 1);
            });
        }
         if (type === "guestPasses") {
            SecurityApi.GuestPassesCreateNewRow(tempGuestPassName, selectedReport.report.report_id, tempPassNo, user.user_id, tempGuestPassIssuedDate, tempGuestPassReturnDate).then(res => {
                setEditGuestPassesRow(selectedReport.guest_passes.length + 1);
            });
        }
    }
    const deleteRow = (type: string, index: number) => {
        if (type === "movement") {
            const movement = selectedReport.movements[index - 1];
            SecurityApi.AircraftMovementsRemoveRow(movement.movement_id, user.user_id).then(res => {
                refresh();
                setEditMovementsRow(-1);
            });
        }
        if (type === "callOut") {
            const callOut = selectedReport.call_outs[index - 1];
            SecurityApi.CallOutRemoveRow(callOut.call_outs_id, user.user_id).then(res => {
                refresh();
                setEditCallOutRow(-1);
            });
        }
        if (type === "walkAround") {
            const walkAround = selectedReport.walk_arounds[index - 1];
            SecurityApi.WalkAroundRemoveRow(walkAround.walk_around_id, user.user_id).then(res => {
                refresh();
                setEditWalkAroundsRow(-1);
            });
        }
        if (type === "incident") {
            const incident = selectedReport.other_incidents[index - 1];
            SecurityApi.OtherIncidentsRemoveRow(incident.other_incident_id, user.user_id).then(res => {
                refresh();
                setEditIncidentsRow(-1);
            });
        }
         if (type === "guestPasses") {
            const guestPasses = selectedReport.guest_passes[index - 1];
            SecurityApi.GuestPassesRemoveRow(guestPasses.id).then(res => {
                refresh();
                setTempGuestPassName("");
                setTempPassNo("");
                setTempResponsibleEmployee(null);
                setTempGuestPassIssuedDate(new Date());
                setTempGuestPassReturnDate(null);
                setTempGuestPassReturned(false);
                setEditGuestPassesRow(-1);
            });
        }
    }

    return (
        <div className="flex flex-wrap w-full ml-6 mt-6">
            <div className="w-full">
                <ReportInfo submitReport={() => submitReport()} selectedReport={selectedReport} closeReport={() => closeReport()} isSecurityAdmin={isSecurityAdmin} />
            </div>
            <AmountControls selectedReport={selectedReport} isSecurityAdmin={isSecurityAdmin} refresh={() => refresh()} user={user.user_id} />
            <div className="grid grid-cols-1 xl:grid-cols-2 w-full gap-2 mt-4 pr-2">
                <div className="mb-4">
                    <Typography text="Aircraft Movements" variant="h2" />
                    <Table
                        headers={["Owner", "Reason", "Tail", "Bay", "In", "Last Modified", ""]}
                        rows={selectedReport.movements}
                        generateBody={(row: any, index: number) => generateMovementsBody(row, index)}
                        pagination={false}
                        skipIndexCol={true}
                    />
                    {isSecurityAdmin && !selectedReport.report.closed &&
                        <div className="flex flex-col items-center">
                            <IconButton onClick={() => addRow("movement")} style={{ color: palette.success }} >
                                <AddCircle />
                            </IconButton>
                        </div>
                    }
                </div>
                <div className="mb-4">
                    <Typography text="Other Incidents" variant="h2" />
                    <Table
                        headers={["Owner", "Description", "Last Modified", ""]}
                        rows={selectedReport.other_incidents}
                        generateBody={(row: any, index: number) => generateIncidentsBody(row, index)}
                        pagination={false}
                        skipIndexCol={true}
                    />
                    {isSecurityAdmin && !selectedReport.report.closed &&
                        <div className="flex flex-col items-center">
                            <IconButton onClick={() => addRow("incident")} style={{ color: palette.success }} >
                                <AddCircle />
                            </IconButton>
                        </div>
                    }
                </div>
                <div className="mb-4">
                    <Typography text="Walkarounds" variant="h2" />
                    <Table
                        headers={["Owner", "Note", "Started", "Completed", "Last Modified", ""]}
                        rows={selectedReport.walk_arounds}
                        generateBody={(row: any, index: number) => generateWalkAroundBody(row, index)}
                        pagination={false}
                        skipIndexCol={true}
                    />
                    {isSecurityAdmin && !selectedReport.report.closed &&
                        <div className="flex flex-col items-center">
                            <IconButton onClick={() => addRow("walkAround")} style={{ color: palette.success }} >
                                <AddCircle />
                            </IconButton>
                        </div>
                    }
                </div>
                <div className="mb-4">
                    <Typography text="Callouts" variant="h2" />
                    <Table
                        headers={["Employee", "Reason", "In", "Out", "Last Modified", ""]}
                        rows={selectedReport.call_outs}
                        generateBody={(row: any, index: number) => generateCallOutBody(row, index)}
                        pagination={false}
                        skipIndexCol={true}
                    />
                    {isSecurityAdmin && !selectedReport.report.closed &&
                        <div className="flex flex-col items-center">
                            <IconButton onClick={() => addRow("callOut")} style={{ color: palette.success }} >
                                <AddCircle />
                            </IconButton>
                        </div>
                    }
                </div>
                <div className="mb-4">
                    <Typography text="Guest Passes" variant="h2" />
                    <Table
                        headers={["Name", "Pass no", "Responsible person", "Issued", "Returned", "Returned in", ""]}
                        rows={selectedReport.guest_passes}
                        generateBody={(row: guestPasses, index: number) => generateGestPassesBody(row, index)}
                        pagination={false}
                        skipIndexCol={true}
                    />
                    {isSecurityAdmin && !selectedReport.report.closed &&
                        <div className="flex flex-col items-center">
                            <IconButton onClick={() => addRow("guestPasses")} style={{ color: palette.success }} >
                                <AddCircle />
                            </IconButton>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
};
export default Report;
